import {observable, action, computed, makeObservable} from 'mobx';
import {apiService} from '../services/api.service';
import {history} from 'fn-common-ui';
import config from 'config';
import _ from 'lodash';

const CROSS_TAB_LOGOUT_KEY = 'firstnet.trainingPortal.voluntaryLogout';

class UserStore {

  constructor() {
    makeObservable(this);
    this.handleStorageEvent = this.handleStorageEvent.bind(this);
    window.addEventListener('storage', this.handleStorageEvent);
  }

  handleStorageEvent(event) {
    if (!event || event.key !== CROSS_TAB_LOGOUT_KEY || !event.newValue) {
      return;
    }
    let payload;
    try {
      payload = JSON.parse(event.newValue);
    } catch (e) {
      return;
    }
    if (payload.type !== 'VOLUNTARY_LOGOUT') {
      return;
    }
    this.handleCrossTabLogout(payload.mode === 'CSP' ? 'CSP' : 'HALO');
  }

  broadcastVoluntaryLogout(mode = 'HALO') {
    const payload = {
      type: 'VOLUNTARY_LOGOUT',
      mode,
      timestamp: Date.now()
    };
    localStorage.setItem(CROSS_TAB_LOGOUT_KEY, JSON.stringify(payload));
  }

  @action handleCrossTabLogout(mode = 'HALO') {
    const logoutUrl = mode === 'CSP' ? config.cspLogout : config.haloLogout;
    sessionStorage.clear();
    window.location.replace(logoutUrl);
  }

   @action authenticateUserWithCsp(jwtToken) {
    return this.authenticateUser(jwtToken, 'CSP');
  }

  @action authenticateUserWithHalo(jwtToken) {
    return this.authenticateUser(jwtToken, 'HALO');
  }

  @action authenticateUser(jwtToken, mode = 'HALO') {
    if(jwtToken) {
      //Don't overwrite the one provided by the other login service
      this.jwt_token = jwtToken;
    }

    const success = (res) => {
      this.handleProfileSuccess(res);
    }

    const fail = (err) => {
      this.handleProfileFail(mode, err)
    }

    this.awaitingValidation = true;
    this.validationPromise = apiService.fetchProfile().then(success, fail);
    return this.validationPromise;
  }

  @action reauthenticateUser(mode = 'HALO') {
    //throttle validation
    if (this.awaitingValidation) {
      //return promise to execute once flag is cleared
      return this.validationPromise;
    } else {
      if(mode === 'CSP' || this.isInAdminPerspective) {
        // changed from authenticateUserWithCsp()  to authenticateUserWithHalo() for redirection to halo
        return this.authenticateUserWithHalo();
      } else {
        return this.authenticateUserWithHalo();
      }
    }
  }

  @action handleProfileSuccess(response) {
    this.initUserObject(response.data);
    this.awaitingValidation = false;
    this.validationPromise = '';
  }

  @action handleProfileFail(mode = 'HALO', err) {
    this.awaitingValidation = false;
    this.validationPromise = '';
    if (err.response && err.response.status === 401) {
      if (this.userValidationDone) {
        //Redirect to session timeout page
       // history.replace('/error/session-timeout');
        window.location.replace(config.haloLogin);
        throw new Error('Session timed out');
      } else {
        if(mode === 'CSP') {
          window.location.replace(config.cspLogin);
          throw new Error('Auth failed - redirecting to CSP login...');
        } else if (mode === 'HALO') {
          window.location.replace(config.haloLogin);
          throw new Error('Auth failed - redirecting to SSO login...');
        }
      }
    } else if (err.response && err.response.status === 403) {
      //this is not an authorized user for anything
      this.user.roles = [];
      this.userValidationDone = true;
      throw new Error('Auth failed - not a Halo or CSP user');
    }
    this.awaitingValidation = false;
    this.validationPromise = '';
  }

  @action logoutUser(mode = 'HALO') {
    let logoutUrl;
    if(mode === 'CSP') {
      logoutUrl = config.cspLogout;
    } else {
      logoutUrl = config.haloLogout;
    }
    this.broadcastVoluntaryLogout(mode);
    apiService.logoutUser().then(() => {
      sessionStorage.clear();
      window.location.replace(logoutUrl);
     // this.resetLeadCaptureFormCookie();
    }).catch((err) => {
      console.error('Received error on logout: ', err);
      window.location.replace(logoutUrl); //go to logout anyway
    });
  }

  @action initUserObject(bearerToken) {
    this.bearer_token = bearerToken;
    let tokenArray = bearerToken.split('.');
    let rawUserObject = JSON.parse(window.atob(tokenArray[1]));
    this.conditionUserObject(rawUserObject);
  }

  conditionUserObject(userInfo) {
    this.user.uid = userInfo.id || '';
    this.user.email = userInfo.email || '';
    this.user.firstName = userInfo.firstName || '';
    this.user.lastName = userInfo.lastName || '';
    this.user.username = userInfo.username || '';
    this.user.phone = userInfo.phone || '';
    this.user.trainingPortalAdmin = userInfo.trainingPortalAdmin || 'N';
    // check if FAN mapping has occurred and designate internal PSE roles
    if (userInfo.authorizations.length) {
      this.user.pse = userInfo.authorizations[0].pseId || '';
      this.user.pseName = userInfo.authorizations[0].pseName || '';
      this.user.roles = userInfo.authorizations[0].pseUserRoles || [];
      console.log('User is logged in with roles: ' + this.user.roles.toString());
    } else {
      // FAN mapping hasn't happened; use HALO provided groups and roles
      this.user.pse = '';
      this.user.pseName = '';
      this.user.roles = userInfo.roles || [];
    }
    this.userValidationDone = true;
  }

  checkRolesString = (allowedRoles) => {
    if(!this.user || !this.user.roles) {
      return false;
    }
    if(_.intersection(allowedRoles, this.user.roles).length > 0) {
      return true;
    }
    return false;
  }

  @computed get isAuthenticHaloUser() {
    return this.checkRolesString(['G_FN','G_FN_IM','G_FN_ADM','G_FN_SUB','G_FN_VOL_ADM','G_FN_VOL','G_FN_ITM']);
  }

  @computed get isAuthenticCspUser() {
    return this.checkRolesString(['G_FN_ADM']);
  }

  @computed get isEditor() {
    return this.user.trainingPortalAdmin === 'Y';
  }

  @computed get isInAdminPerspective() {
    return window.location.href.indexOf('/admin') > -1 || window.location.href.indexOf('/content/') > -1;
  }

  @observable user = {};
  @observable bearer_token = '';
  @observable jwt_token = '';
  @observable userValidationDone = false;
  @observable validationPromise = '';
}

export const userStore = new UserStore();